import { compact } from 'lodash';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { Filters_DEPRECATED } from '~/components/Shared/Filters_DEPRECATED/Filters_DEPRECATED';
import { PersistedTable } from '~/components/Shared/PersistedTable/PersistedTable';
import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { Button } from '~/eds/src/components/Button/Button';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Text } from '~/eds/src/components/Text/Text';
import { Tooltip } from '~/eds/src/components/Tooltip/Tooltip';
import { AutomationStageType } from '~/enums/AutomationStageType';
import { AutomationStatusType } from '~/enums/AutomationStatusType';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { QueryParamType } from '~/enums/QueryParamType';
import { TableContextType } from '~/enums/TableContextType';
import { isActiveQuery } from '~/features/advanced-search/query-filters/QueryFilters.utils';
import { QueryFiltersFormField } from '~/features/advanced-search/query-filters/QueryFiltersFormField';
import { useRestrictedFilterIds } from '~/features/quick-ai/hooks';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useClientSubDomain } from '~/hooks/useClientSubDomain';
import { useHasFeatureFlag } from '~/hooks/useHasFeatureFlag';
import { useResolveUsers } from '~/hooks/useResolveUsers';
import { useTableSettings } from '~/hooks/useTableSettings';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import searchSlice from '~/redux/slices/search';
import { useRouting } from '~/routing/useRouting';
import { redirectToPage } from '~/services/redirects';
import { ERROR, SUCCESS } from '~/types/toast.types';
import FlexLayout from '~/ui/components/FlexLayout/FlexLayout';
import LoadingContainer from '~/ui/components/LoadingContainer/LoadingContainer';
import LoadingSpinner from '~/ui/components/LoadingSpinner/LoadingSpinner';
import TextInput from '~/ui/components/TextInput/TextInput';
import { useModal } from '~/ui/hooks/modal';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';
import { toLowercaseTrimmed } from '~/utils/strings';

import { buildClauseAnalyzerParams, getShortName } from '../util';
import { SampleContentCell } from './SampleContentCell';

const HIGHLIGHT_STATUS_ID = 'highlightStatus';

const ALL_STATUSES = [
  AutomationStatusType.ACTIVE,
  AutomationStatusType.SUSPENDED,
];

const filtersOptions = {
  disableManageFilters: true,
  disableRemoveFilter: true,
  disableClearFilters: true,
};

const AutomationHubQuickAiPage = () => {
  const intl = useIntl();
  const { toast } = useToast();
  const dispatch = useDispatch();
  const { navigate } = useRouting();

  const deleteText = intl.formatMessage({
    id: 'AutomationHubQuickAiPage.deleteAction',
    defaultMessage: 'Delete',
    description: 'Action label for delete modal',
  });

  const suspendText = intl.formatMessage({
    id: 'AutomationHubQuickAiPage.suspendAction',
    defaultMessage: 'Suspend',
    description: 'Action label for suspend modal',
  });
  const HIGHLIGHT_STATUS_OPTIONS = [
    {
      label: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.activeStatus1',
        defaultMessage: 'Active',
        description: 'Label for active status in Quick AI filters',
      }),
      value: AutomationStatusType.ACTIVE,
    },
    {
      label: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.suspendedStatus1',
        defaultMessage: 'Suspended',
        description: 'Label for suspended status in Quick AI filters',
      }),
      value: AutomationStatusType.SUSPENDED,
    },
  ];

  const [selected, setSelected] = useState();
  const [deleteConfirm, setDeleteConfirm] = useState();

  const pagination = useSelector(
    slices.automation.selectors.selectAutomationSamplePagination,
  );
  const clause = useSelector(
    slices.automation.selectors.selectAutomationClause,
  );
  const quickAiForm = useSelector(
    slices.automation.selectors.selectAutomationQuickAiForm,
  );

  const hasAdvancedSearch = useHasFeatureFlag(FeatureFlagType.SearchV2);
  const hasAdvancedSearchV3 = useFlag(FlagType.SearchV3);
  const enableQuickAiTableSorting = useFlag(FlagType.QuickAiTableSorting);
  const enableQuickAiFilters = useFlag(FlagType.QuickAiFilters);

  const { tableSettings } = useTableSettings(TableContextType.AutomationHub);
  useEffect(() => {
    if (tableSettings) {
      dispatch(
        slices.automation.actions.setAutomationSamplePageSize({
          pageSize: tableSettings.pageSize,
        }),
      );
      // TODO: set sorting from table settings when we plan to enable QuickAiTableSorting flag
    }
  }, [tableSettings]);

  const defaultStatus =
    clause?.status === AutomationStatusType.SUSPENDED
      ? ALL_STATUSES
      : [AutomationStatusType.ACTIVE];

  const [status, setStatus] = useState(defaultStatus);

  const subdomain = useClientSubDomain();

  const clauseName = getPageSearchQueryByKey(
    QueryParamType.Clause,
    clause.name,
  );

  useEffect(() => {
    if (!!clauseName)
      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          name: clauseName,
        }),
      );
  }, [clauseName]);

  const { data: idf, isFetching: isFetchingIdf } =
    api.endpoints.getIdentifications.useQuery(
      { name: clauseName },
      { skip: !clauseName },
    );

  const restrictedClauseFilterIds = useRestrictedFilterIds();

  const INITIAL_FILTERS = idf?.filters ?? [];

  const [selectedQuery, setSelectedQuery] = useState(INITIAL_FILTERS);

  const { data: sectionsAndFolders, isFetching: isFetchingSections } =
    api.endpoints.getFilterSections.useQuery(undefined);

  useEffect(() => {
    if (!isFetchingIdf && idf) {
      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          status: idf.bulkTaggingEnabledStatus,
        }),
      );

      if (idf.bulkTaggingEnabledStatus === AutomationStatusType.SUSPENDED) {
        setStatus(ALL_STATUSES);
      }
    }
  }, [idf, isFetchingIdf]);

  useEffect(() => {
    setSelectedQuery(INITIAL_FILTERS);
  }, [idf]);

  const { data: allSamples, isFetching: isFetchingAllSamples } =
    api.endpoints.getSamples.useQuery({
      ...pagination,
      status: ALL_STATUSES,
      clause: clauseName,
    });

  const dataExists = !!allSamples && allSamples.count > 0;

  const { data, error, isFetching } = api.endpoints.getSamples.useQuery(
    {
      ...pagination,
      status,
      clause: clauseName,
    },
    { skip: !dataExists },
  );

  const { users: resolvedUsers, isFetching: isFetchingUsers } = useResolveUsers(
    {
      userIds: data?.results.map((sample) => sample.createdBy),
    },
  );

  const statusHasNoData = !!data && data.count <= 0;

  const buildClauseModalTitle = (op, name) => {
    if (name.length < 50) {
      return (
        <FlexLayout>
          {intl.formatMessage(
            {
              id: 'AutomationHubQuickAiPage.clauseModalTitle',
              defaultMessage: '{op} Clause: {name}?',
              description:
                'Title for clause modal. op is the operation, name is the clause name. Example: op = "Delete", name = "Assignment"',
            },
            { op, name },
          )}
        </FlexLayout>
      );
    }

    return (
      <FlexLayout>
        {intl.formatMessage(
          {
            id: 'AutomationHubQuickAiPage.clauseModalTitleWithTooltip',
            defaultMessage: '{op} Clause: ',
            description:
              'Title for clause modal with tooltip for clause name. op is the operation. Example: op = "Delete"',
          },
          { op },
        )}
        <Tooltip tooltip={name}>{getShortName(name, 50)}</Tooltip>?
      </FlexLayout>
    );
  };

  const buildSampleModalTitle = (op, name) => {
    if (name.length <= 0) {
      return (
        <FlexLayout>
          {intl.formatMessage(
            {
              id: 'AutomationHubQuickAiPage.sampleModalTitleNoName',
              defaultMessage: '{op} Sample?',
              description:
                'Title for sample modal without sample name. Example: op = "Delete"',
            },
            { op },
          )}
        </FlexLayout>
      );
    }

    if (name.length < 50) {
      return (
        <FlexLayout>
          {intl.formatMessage(
            {
              id: 'AutomationHubQuickAiPage.sampleModalTitle',
              defaultMessage: '{op} Sample: {name}?',
              description:
                'Title for sample modal. op is the operation, name is the sample name. Example: op = "Delete", name = "Assignment"',
            },
            { op, name },
          )}
        </FlexLayout>
      );
    }

    return (
      <FlexLayout>
        {intl.formatMessage(
          {
            id: 'AutomationHubQuickAiPage.sampleModalTitleWithTooltip',
            defaultMessage: '{op} Sample: ',
            description:
              'Title for sample modal with tooltip for sample name. op is the operation. Example: op = "Delete"',
          },
          { op },
        )}
        <Tooltip tooltip={name}>{getShortName(name, 50)}</Tooltip>?
      </FlexLayout>
    );
  };

  /***** clause actions *****/
  const [deleteClause, deleteClauseResult] =
    api.endpoints.deleteClause.useMutation();
  const { isError: isDeleteClauseError, isSuccess: isDeleteClauseSuccess } =
    deleteClauseResult;

  const [suspendClause, suspendClauseResult] =
    api.endpoints.suspendClause.useMutation();
  const { isError: isSuspendClauseError, isSuccess: isSuspendClauseSuccess } =
    suspendClauseResult;

  const [resumeClause, resumeClauseResult] =
    api.endpoints.resumeClause.useMutation();
  const { isError: isResumeClauseError, isSuccess: isResumeClauseSuccess } =
    resumeClauseResult;

  const [updateClauseFilters, updateClauseFiltersResults] =
    api.endpoints.updateClause.useMutation();

  const {
    isError: isUpdateClauseFiltersError,
    isSuccess: isUpdateClauseFiltersSuccess,
  } = updateClauseFiltersResults;

  useEffect(() => {
    if (isDeleteClauseSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AutomationHubQuickAiPage.clauseDeletedSuccess',
            defaultMessage: '{clauseName} Clause deleted successfully.',
            description:
              'Toast message for successfull clause deletion. clauseName is the short name of the clause. Example: clauseName = "Assignment"',
          },
          { clauseName: getShortName(clauseName ?? '') },
        ),
      );
    }
    if (isDeleteClauseError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.deleteClauseFailed',
          defaultMessage: 'Delete Clause failed. Please try again.',
          description: 'Toast message for failed clause deletion',
        }),
      );
    }
  }, [isDeleteClauseSuccess, isDeleteClauseError]);

  useEffect(() => {
    if (isSuspendClauseSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AutomationHubQuickAiPage.clauseSuspendedSuccess',
            defaultMessage: '{clauseName} Clause suspended successfully.',
            description:
              'Toast message for successful clause suspension. clauseName is the short name of the clause. Example: clauseName = "Assignment"',
          },
          { clauseName: getShortName(clauseName ?? '') },
        ),
      );
      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          status: AutomationStatusType.SUSPENDED,
        }),
      );
    }
    if (isSuspendClauseError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.suspendClauseFailed',
          defaultMessage: 'Suspend Clause failed. Please try again.',
          description: 'Toast message for failed clause suspension',
        }),
      );
    }
  }, [isSuspendClauseSuccess, isSuspendClauseError]);

  useEffect(() => {
    if (isUpdateClauseFiltersSuccess) {
      toast({
        status: 'success',
        title: intl.formatMessage({
          id: 'AutomationHubQuickAiPage.filtersUpdatedSuccess',
          defaultMessage: 'Filters updated successfully',
          description: 'Toast message for successful filter update',
        }),
      });
      if (isUpdateClauseFiltersError) {
        toast({
          status: 'danger',
          title: intl.formatMessage({
            id: 'AutomationHubQuickAiPage.filtersUpdateFailed',
            defaultMessage: 'Failed to update filters',
            description: 'Toast message for failed filter update',
          }),
        });
      }
    }
  }, [isUpdateClauseFiltersSuccess, isUpdateClauseFiltersError]);

  useEffect(() => {
    if (isResumeClauseSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage(
          {
            id: 'AutomationHubQuickAiPage.clauseReactivatedSuccess',
            defaultMessage: '{clauseName} Clause reactivated successfully.',
            description:
              'Toast message for successful clause reactivation. clauseName is the short name of the clause. Example: clauseName = "Assignment"',
          },
          { clauseName: getShortName(clauseName ?? '') },
        ),
      );
      dispatch(
        slices.automation.actions.setAutomationClause({
          ...clause,
          status: AutomationStatusType.ACTIVE,
        }),
      );
    }
    if (isResumeClauseError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.resumeClauseFailed',
          defaultMessage: 'Resume Clause failed. Please try again.',
          description: 'Toast message for failed clause reactivation',
        }),
      );
    }
  }, [isResumeClauseSuccess, isResumeClauseError]);

  const [deleteClauseModal, showDeleteClauseModal] = useModal({
    title: buildClauseModalTitle(deleteText, clauseName ?? ''),
    onHide: () => setDeleteConfirm(),
    onCancel: () => setDeleteConfirm(),
    actionButton: {
      disabled: deleteConfirm !== 'DELETE',
      text: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.deleteClauseButton',
        defaultMessage: 'Permanently delete',
        description: 'Button text for deleting a clause',
      }),
      variant: 'red',
      promise: async () => deleteClause({ name: clauseName }),
    },
    content: (
      <FlexLayout flexDirection="column" space={4}>
        {isFetchingIdf ? (
          <LoadingSpinner size="medium" />
        ) : (
          <>
            <Text variant="body">
              {intl.formatMessage(
                {
                  id: 'AutomationHubQuickAiPage.clauseDeleteDescription',
                  defaultMessage:
                    'This Clause has {count} {count, plural, one {Sample} other {Samples}} and {identificationCount} {identificationCount, plural, one {Identification} other {Identifications}}. Deleting this Clause will permanently delete all its Samples and their Identifications. You won’t be able to revert this action.',
                  description:
                    'Description for clause delete modal. sampleCount is the count and pluralized word for samples, identificationCount is the count and pluralized word for identifications. Example: sampleCount = "1 Sample", identificationCount = "2 Identifications"',
                },
                {
                  count: allSamples?.count ?? 0,
                  identificationCount:
                    idf?.bulkTaggingTotalIdentifications ?? 0,
                },
              )}
            </Text>
            <Text variant="body">
              {intl.formatMessage({
                id: 'AutomationHubQuickAiPage.clauseDeletePrompt',
                defaultMessage:
                  'You won\'t be able to revert this action. If you are sure, please type "DELETE" in the box below:',
                description: 'Prompt for clause delete modal',
              })}
            </Text>
            <TextInput
              width="fullWidth"
              value={deleteConfirm}
              onChange={setDeleteConfirm}
            />
          </>
        )}
      </FlexLayout>
    ),
  });

  const [suspendClauseModal, showSuspendClauseModal] = useModal({
    title: buildClauseModalTitle(suspendText, clauseName ?? ''),
    actionButton: {
      disabled: false,
      text: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.suspendClauseButton',
        defaultMessage: 'Suspend the Clause',
        description: 'Button text for suspending a clause',
      }),
      promise: async () => suspendClause({ name: clauseName }),
    },
    content: (
      <FlexLayout flexDirection="column" space={4}>
        <Text variant="body">
          {intl.formatMessage({
            id: 'AutomationHubQuickAiPage.suspendClausePrompt',
            defaultMessage:
              ' Are you sure you would like Evisort to stop identifying new occurrences of this clause?',
            description: 'Prompt for clause suspension modal',
          })}
        </Text>
        <Text variant="body">
          <FormattedMessage
            id="AutomationHubQuickAiPage.suspendClauseNote"
            defaultMessage=" <strong>Note</strong>: All Samples under this clause will be
          suspended."
            description="Note for clause suspension modal"
            values={{
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </Text>
      </FlexLayout>
    ),
  });

  const [resumeClauseModal, showResumeClauseModal] = useModal({
    title: buildClauseModalTitle(
      intl.formatMessage({
        id: 'AutomationHubQuickAiPage.reactivateAction',
        defaultMessage: 'Reactivate',
        description: 'Action for clause reactivation modal title',
      }),
      clauseName ?? '',
    ),
    actionButton: {
      disabled: false,
      text: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.reactivateClauseButton',
        defaultMessage: 'Reactivate the Clause',
        description: 'Button text for reactivating a clause',
      }),
      promise: async () => resumeClause({ name: clauseName }),
    },
    content: (
      <FlexLayout flexDirection="column" space={4}>
        <Text variant="body">
          {intl.formatMessage({
            id: 'AutomationHubQuickAiPage.reactivateClausePrompt',
            defaultMessage:
              'Are you sure you would like Evisort to resume identifying new occurrences of this clause?',
            description: 'Prompt for clause reactivation modal',
          })}
        </Text>
        <Text variant="body">
          <FormattedMessage
            id="AutomationHubQuickAiPage.reactivateClauseNote"
            defaultMessage="<strong>Note</strong>: All Samples associated with this clause will be reactivated immediately and will run on existing documents. If you'd like to control which Samples to reactivate, click <strong>Cancel</strong> and go into the Clause to look into each Sample separately."
            description="Note for clause reactivation modal"
            values={{
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </Text>
      </FlexLayout>
    ),
  });

  /***** sample actions *****/
  const [deleteSample, deleteResult] = api.endpoints.deleteSample.useMutation();
  const { isError: isDeleteError, isSuccess: isDeleteSuccess } = deleteResult;

  const [suspendSample, suspendResult] =
    api.endpoints.suspendSample.useMutation();
  const { isError: isSuspendError, isSuccess: isSuspendSuccess } =
    suspendResult;

  const [resumeSample, resumeResult] = api.endpoints.resumeSample.useMutation();
  const { isError: isResumeError, isSuccess: isResumeSuccess } = resumeResult;

  useEffect(() => {
    if (isDeleteSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.sampleDeletedSuccess',
          defaultMessage: '{name} Sample deleted successfully.',
          description:
            'Success message for sample model deletion. Example: name = "Standard Sample"',
          values: {
            name: getShortName(selected?.highlightName ?? ''),
          },
        }),
      );
    }
    if (isDeleteError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.deleteSampleFailed',
          defaultMessage: 'Delete Sample failed. Please try again.',
          description: 'Error message for failed sample deletion',
        }),
      );
    }
  }, [isDeleteSuccess, isDeleteError]);

  useEffect(() => {
    if (isSuspendSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.sampleSuspendedSuccess',
          defaultMessage: '{name} Sample suspended successfully.',
          description:
            'Success message for sample suspension.  Example: name = "Standard Sample"',
          values: {
            name: getShortName(selected?.highlightName ?? ''),
          },
        }),
      );
    }
    if (isSuspendError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.suspendSampleFailed',
          defaultMessage: 'Suspend Sample failed. Please try again.',
          description: 'Error message for failed sample suspension',
        }),
      );
    }
  }, [isSuspendSuccess, isSuspendError]);

  useEffect(() => {
    if (isResumeSuccess) {
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.sampleReactivatedSuccess',
          defaultMessage: '{name} Sample reactivated successfully.',
          description:
            'Success message for sample reactivation. Example: name = "Standard Sample"',
          values: {
            name: getShortName(selected?.highlightName ?? ''),
          },
        }),
      );
    }
    if (isResumeError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'AutomationHubQuickAiPage.resumeSampleFailed',
          defaultMessage: 'Resume Sample failed. Please try again.',
          description: 'Error message for failed sample reactivation',
        }),
      );
    }
  }, [isResumeSuccess, isResumeError]);

  const [deleteModal, showDeleteModal] = useModal({
    title: buildSampleModalTitle(deleteText, selected?.highlightName ?? ''),
    onHide: () => setSelected(),
    onCancel: () => setSelected(),
    actionButton: {
      disabled: false,
      text: deleteText,
      variant: 'red',
      promise: async () => deleteSample({ id: selected.id }),
    },
    content: (
      <FlexLayout flexDirection="column" space={4}>
        {selected?.highlightName ? (
          <>
            <Text variant="body">
              <FormattedMessage
                id="AutomationHubQuickAiPage.sampleDeleteDescriptionNamed"
                defaultMessage="Sample <strong>{sampleName}</strong> has identified {count} {count, plural, one {occurrence} other {occurrences}} of Clause <strong>{clauseName}</strong> in your documents."
                description="Description for sample delete modal with sample name. sampleName is the name of the sample. count is the number of occurrences. clauseName is the name of the clause. Example: sampleName = 'Assignment', count = 1, clauseName = 'Confidentiality'"
                values={{
                  sampleName: selected?.highlightName,
                  count: selected?.discoveries ?? 0,
                  clauseName: clauseName,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Text>
            <Text variant="body">
              <FormattedMessage
                id="AutomationHubQuickAiPage.sampleDeleteHighlightingNamed"
                defaultMessage="If you delete <strong>{sampleName}</strong>, then the highlighting for <strong>{clauseName}</strong> that <strong>{sampleName}</strong> identified will be removed from all documents."
                description="Description for sample delete modal highlighting removal. sampleName is the name of the sample"
                values={{
                  sampleName: selected?.highlightName,
                  clauseName: clauseName,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Text>
            <Text variant="body">
              {intl.formatMessage({
                id: 'AutomationHubQuickAiPage.sampleDeleteUndoNamed',
                defaultMessage: "You won't be able to undo this action.",
                description:
                  'Undo warning for sample delete modal with sample name',
              })}
            </Text>
          </>
        ) : (
          <>
            <Text variant="body">
              <FormattedMessage
                id="AutomationHubQuickAiPage.sampleDeleteDescriptionUnnamed"
                defaultMessage="This Sample has identified {count} {count, plural, one {occurrence} other {occurrences}} of Clause '<strong>{clauseName}</strong>' in your documents."
                description="Description for sample delete modal without sample name. count is the number of occurrences. clauseName is the name of the clause. Example: count = 1, clauseName = 'Confidentiality'"
                values={{
                  count: selected?.discoveries ?? 0,
                  clauseName: clauseName,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Text>
            <Text variant="body">
              <FormattedMessage
                id="AutomationHubQuickAiPage.sampleDeleteHighlightingUnnamed"
                defaultMessage="If you delete <strong>{sampleName}</strong>, then the highlighting for <strong>{clauseName}</strong> that <strong>{sampleName}</strong> identified will be removed from all documents."
                description="Description for sample delete modal highlighting removal. sampleName is the name of the sample. clauseName is the name of the clause. Example: sampleName = 'Assignment', clauseName = 'Confidentiality'"
                values={{
                  sampleName: selected?.highlightName,
                  clauseName: clauseName,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            </Text>
            <Text variant="body">
              {intl.formatMessage({
                id: 'AutomationHubQuickAiPage.sampleDeleteUndoUnnamed',
                defaultMessage: "You won't be able to undo this action.",
                description:
                  'Undo warning for sample delete modal without sample name',
              })}
            </Text>
          </>
        )}
      </FlexLayout>
    ),
  });

  const [suspendModal, showSuspendModal] = useModal({
    title: buildSampleModalTitle(suspendText, selected?.highlightName ?? ''),
    onHide: () => setSelected(),
    onCancel: () => setSelected(),
    actionButton: {
      disabled: false,
      text: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.suspendSampleButton',
        defaultMessage: 'Suspend the Sample',
        description: 'Button text for sample suspend modal',
      }),
      promise: async () => suspendSample({ id: selected.id }),
    },
    content: (
      <Text variant="body">
        {intl.formatMessage({
          id: 'AutomationHubQuickAiPage.suspendSampleDescription',
          defaultMessage:
            'Are you sure you would like Evisort to stop identifying new occurrences of this Sample?',
          description: 'Description for sample suspend modal',
        })}
      </Text>
    ),
  });

  const [resumeModal, showResumeModal] = useModal({
    title: buildSampleModalTitle(
      intl.formatMessage({
        id: 'AutomationHubQuickAiPage.resumeAction',
        defaultMessage: 'Reactivate',
        description: 'Action for sample resume modal title',
      }),
      selected?.highlightName ?? '',
    ),
    onHide: () => setSelected(),
    onCancel: () => setSelected(),
    actionButton: {
      disabled: false,
      text: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.resumeSampleButton',
        defaultMessage: 'Reactivate the Sample',
        description: 'Button text for sample resume modal',
      }),
      promise: async () => resumeSample({ id: selected.id }),
    },
    content: (
      <FlexLayout flexDirection="column" space={4}>
        <Text variant="body">
          {intl.formatMessage({
            id: 'AutomationHubQuickAiPage.resumeSampleDescription',
            defaultMessage:
              'Are you sure you would like Evisort to resume identifying new occurrences of this Sample?',
            description: 'Description for sample resume modal',
          })}
        </Text>
        <Text variant="body">
          <FormattedMessage
            id="AutomationHubQuickAiPage.resumeSampleNote"
            defaultMessage="<strong>Note</strong>: This will run on existing documents."
            description="Note for sample resume modal"
            values={{
              strong: (chunks) => <strong>{chunks}</strong>,
            }}
          />
        </Text>
      </FlexLayout>
    ),
  });

  const onSetStatusFilter = (filters) => {
    const values = filters[0]?.values ?? [];
    setStatus(values.length > 0 ? values : defaultStatus);
  };

  const onCreateNewSample = () => {
    dispatch(
      slices.automation.actions.setAutomationQuickAiDataForm({
        ...quickAiForm,
        name: clauseName,
      }),
    );
    dispatch(
      slices.automation.actions.setAutomationManageStage(
        AutomationStageType.QuickAiForm,
      ),
    );
  };

  const redirectToAnalyzerResults = ({ name }) => {
    const params = new URLSearchParams(
      buildClauseAnalyzerParams(name),
    ).toString();
    redirectToPage(subdomain, `analyzer/results?${params}`);
  };

  const redirectToAdvancedSearch = ({ name }) => {
    const values = [{ provision: name, text_search: [] }];
    const filters = [
      { id: uuid(), fieldId: 'clause', operatorId: 'contains_any', values },
    ];
    const fields = sectionsAndFolders?.fields;
    if (hasAdvancedSearchV3) {
      dispatch(slices.searchV3.actions.setSelectedFilters(filters));
    } else {
      dispatch(searchSlice.actions.setFilters(filters));
      dispatch(
        searchSlice.actions.setQuery({ booleanQuery: '', filters, fields }),
      );
    }

    navigate('/search');
  };

  const redirectToSearchResults = ({ name }) => {
    if (hasAdvancedSearch) {
      redirectToAdvancedSearch({ name });
    } else {
      redirectToAnalyzerResults({ name });
    }
  };

  const onClauseActs = (action) => {
    switch (action) {
      case 'suspend':
        showSuspendClauseModal();
        break;
      case 'reactivate':
        showResumeClauseModal();
        break;
      case 'delete':
        showDeleteClauseModal();
        break;
      default:
        break;
    }
  };

  const fieldGroups = [];

  const fields = {
    [HIGHLIGHT_STATUS_ID]: {
      id: HIGHLIGHT_STATUS_ID,
      label: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.highlightStatusFilterLabel',
        defaultMessage: 'Status',
        description: 'Label for the highlight status filter',
      }),
      type: 'enum_set',
      whitelistOperatorIds: ['contains_any'],
      overrideOperatorLabels: {
        contains_any: intl.formatMessage({
          id: 'AutomationHubQuickAiPage.containsAnyOperatorLabel',
          defaultMessage: 'includes',
          description: 'Label for the "icludes" operator in filters',
        }),
      },
      settings: {
        __testEnableSelectAll: () => false,
        options: HIGHLIGHT_STATUS_OPTIONS,
      },
    },
  };

  const filters = compact([
    {
      id: HIGHLIGHT_STATUS_ID,
      fieldId: HIGHLIGHT_STATUS_ID,
      operatorId: 'contains_any',
      values: status ?? [],
    },
  ]);

  const rowActions = [
    {
      label: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.suspendRowAction',
        defaultMessage: 'Suspend',
        description: 'Row action label for suspending a sample',
      }),
      onClick: (row) => {
        setSelected(row);
        showSuspendModal();
      },
      condition: (row) => row.status === AutomationStatusType.ACTIVE,
    },
    {
      label: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.reactivateRowAction',
        defaultMessage: 'Reactivate',
        description: 'Row action label for reactivating a sample',
      }),
      onClick: (row) => {
        setSelected(row);
        showResumeModal();
      },
      condition: (row) => row.status === AutomationStatusType.SUSPENDED,
    },
    {
      label: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.deleteRowAction',
        defaultMessage: 'Delete',
        description: 'Row action label for deleting a sample',
      }),
      onClick: (row) => {
        setSelected(row);
        showDeleteModal();
      },
    },
  ];

  const columns = [
    {
      key: 'text',
      title: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.samplesColumnTitle',
        defaultMessage: 'Samples',
        description: 'Column title for samples',
      }),
      width: 'xl',
      disableSortBy: !enableQuickAiTableSorting,
      renderCell: ({ text, highlightName }) => (
        <FlexLayout sx={{ width: '95%' }}>
          <SampleContentCell text={text} sampleName={highlightName} />
        </FlexLayout>
      ),
    },
    {
      key: 'createdOn',
      cellType: 'datetime',
      title: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.createdOnColumnTitle',
        defaultMessage: 'Created On',
        description: 'Column title for created on date',
      }),
      width: 'm',
      disableSortBy: !enableQuickAiTableSorting,
      mapCellProps: ({ createdOn }) => ({
        datetime: createdOn,
      }),
    },
    {
      key: 'createdBy',
      cellType: 'user',
      title: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.createdByColumnTitle',
        defaultMessage: 'Created by',
        description: 'Column title for created by user',
      }),
      width: 'm',
      disableSortBy: !enableQuickAiTableSorting,
      mapCellProps: ({ createdBy }) => ({
        user: resolvedUsers?.[createdBy],
      }),
    },
    {
      key: 'lastDiscovery',
      title: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.lastIdentificationColumnTitle',
        defaultMessage: 'Last Identification',
        description: 'Column title for last identification date',
      }),
      cellType: 'datetime',
      width: 'm',
      disableSortBy: !enableQuickAiTableSorting,
      mapCellProps: ({ lastDiscovery }) => ({
        datetime: lastDiscovery,
      }),
    },
    {
      key: 'discoveries',
      cellType: 'number',
      title: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.identificationsColumnTitle',
        defaultMessage: 'Identifications',
        description: 'Column title for identifications count',
      }),
      width: 'm',
      disableSortBy: !enableQuickAiTableSorting,
      mapCellProps: ({ discoveries }) => ({
        number: discoveries,
      }),
    },
    {
      key: 'status',
      cellType: 'chips',
      title: intl.formatMessage({
        id: 'AutomationHubQuickAiPage.statusColumnTitle',
        defaultMessage: 'Status',
        description: 'Column title for status',
      }),
      width: 'm',
      disableSortBy: !enableQuickAiTableSorting,
      mapCellProps: ({ status }) => {
        const isActive = status === AutomationStatusType.ACTIVE;
        const statusChip = {
          text: isActive
            ? intl.formatMessage({
                id: 'AutomationHubQuickAiPage.activeStatus2',
                defaultMessage: 'Active',
                description: 'Label for active status in Quick AI samples',
              })
            : intl.formatMessage({
                id: 'AutomationHubQuickAiPage.suspendedStatus2',
                defaultMessage: 'Suspended',
                description: 'Label for suspended status in Quick AI samples',
              }),
          status: isActive ? 'success' : 'inactive',
        };
        return { chips: [statusChip] };
      },
    },
  ];

  if (!dataExists) {
    return (
      <LoadingContainer isLoading={isFetchingAllSamples}>
        <FlexLayout flexDirection="column" alignItems="center" mt={4} space={4}>
          <EmptyPage
            preset="new-clause"
            resolveAction={{
              variant: 'primary',
              icon: 'plus',
              iconPosition: 'left',
              text: intl.formatMessage({
                id: 'AutomationHubQuickAiPage.createNewClauseButton',
                defaultMessage: 'New Quick AI',
                description: 'Button text for creating a new Quick AI clause',
              }),
              onClick: () =>
                dispatch(
                  slices.automation.actions.setAutomationManageStage(
                    AutomationStageType.QuickAiForm,
                  ),
                ),
            }}
          />
        </FlexLayout>
      </LoadingContainer>
    );
  }

  if (dataExists && statusHasNoData && !isFetching) {
    showToast(
      SUCCESS,
      intl.formatMessage(
        {
          id: 'AutomationHubQuickAiPage.noSamplesResetFilters',
          defaultMessage:
            'Because you have no {status} samples, we reset your filters to show you all samples.',
          description:
            'Toast message when no samples are found for a status and filters are reset. status is the lowercase status. Example: status = "active"',
        },
        { status: toLowercaseTrimmed(status[0]) },
      ),
    );
    onSetStatusFilter([{ values: ALL_STATUSES }]);
  }

  const hasExistingfilters = Boolean(idf?.filters?.length);
  const activeQuery = isActiveQuery(selectedQuery);

  const handleOnSaveFilters = () => {
    setSelectedQuery(activeQuery);
    updateClauseFilters({
      query: activeQuery,
      clauseName: clause.name,
    });
  };

  return (
    <FlexLayout flexDirection="column" space={4}>
      {enableQuickAiFilters && (
        <QueryFiltersFormField
          title={intl.formatMessage({
            id: 'AutomationHubQuickAiPage.documentFiltersTitle',
            defaultMessage: 'Document Filters',
            description: 'Title for document filters section',
          })}
          description={intl.formatMessage({
            id: 'AutomationHubQuickAiPage.documentFiltersDescription',
            defaultMessage:
              'Filters will be used to select documents that contain this exact clause. Changing filters will impact all tagged samples.',
            description: 'Description for document filters section',
          })}
          defaultFilters={
            hasExistingfilters || activeQuery.length > 0
              ? undefined
              : ['folder', 'Contract Type', 'Effective Date']
          }
          restrictedFilterIds={restrictedClauseFilterIds}
          query={selectedQuery}
          onQueryChange={(updatedQuery) => {
            setSelectedQuery(updatedQuery);
          }}
          optional={false}
          mode="edit"
          onSaveFilters={handleOnSaveFilters}
          onCancel={() => {
            setSelectedQuery(idf?.filters);
          }}
        />
      )}
      <FlexLayout
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        {/* eslint-disable-next-line react/jsx-pascal-case -- deprecating */}
        <Filters_DEPRECATED
          fields={fields}
          filters={filters}
          groups={fieldGroups}
          options={filtersOptions}
          onChange={onSetStatusFilter}
        />
        <FlexLayout space={4}>
          <Button
            variant="action"
            icon="plus"
            iconPosition="left"
            text={intl.formatMessage({
              id: 'AutomationHubQuickAiPage.newSampleButton',
              defaultMessage: 'New Sample',
              description: 'Button text for creating a new sample',
            })}
            onClick={onCreateNewSample}
          />
          <Button
            isLoading={isFetchingSections}
            variant="action"
            icon="view"
            iconPosition="left"
            text={intl.formatMessage(
              {
                id: 'AutomationHubQuickAiPage.viewIdentificationsButton',
                defaultMessage:
                  'View {count} {count, plural, one {Identification} other {Identifications}}',
                description:
                  'Button text for viewing identifications. count is the number of identifications. Example: count = 1',
              },
              { count: idf?.bulkTaggingTotalIdentifications ?? 0 },
            )}
            onClick={() => redirectToSearchResults({ name: clauseName })}
            disabled={(idf?.bulkTaggingTotalIdentifications ?? 0) === 0}
          />
          <FlexLayout>
            <ActionsMenu
              actions={compact([
                clause.status !== AutomationStatusType.SUSPENDED && {
                  label: intl.formatMessage({
                    id: 'AutomationHubQuickAiPage.suspendAllButton',
                    defaultMessage: 'Suspend all',
                    description: 'Button text for suspending all samples',
                  }),
                  value: 'suspend',
                },
                clause.status === AutomationStatusType.SUSPENDED && {
                  label: intl.formatMessage({
                    id: 'AutomationHubQuickAiPage.reactivateAllButton',
                    defaultMessage: 'Reactivate all',
                    description: 'Button text for reactivating all samples',
                  }),
                  value: 'reactivate',
                },
                {
                  label: intl.formatMessage({
                    id: 'AutomationHubQuickAiPage.deleteAllButton',
                    defaultMessage: 'Delete all',
                    description: 'Button text for deleting all samples',
                  }),
                  value: 'delete',
                  isDanger: true,
                },
              ])}
              name={intl.formatMessage({
                id: 'AutomationHubQuickAiPage.moreActionsMenu',
                defaultMessage: 'more actions',
                description: 'Name for more actions menu',
              })}
              onActionClick={onClauseActs}
            />
          </FlexLayout>
        </FlexLayout>
      </FlexLayout>
      <LoadingContainer
        isLoading={isFetching || isFetchingIdf || isFetchingUsers}
      >
        {!error && !!data && (
          <PersistedTable
            context={TableContextType.AutomationHub}
            name="automation-clause-samples"
            columns={columns}
            totalCount={data.count}
            data={data.results}
            rowActions={rowActions}
            state={pagination}
            onUpdate={(pagination) =>
              dispatch(
                slices.automation.actions.setAutomationSamplePagination(
                  pagination,
                ),
              )
            }
            onPageSizeChange={(pageSize) =>
              dispatch(
                slices.automation.actions.setAutomationSamplePageSize({
                  pageSize,
                }),
              )
            }
            options={{
              enableManageColumns: true,
              enableExportXlsx: false,
              enableSelectRows: false,
              enablePageSizeSelect: true,
            }}
          />
        )}
      </LoadingContainer>
      {deleteModal}
      {suspendModal}
      {resumeModal}
      {deleteClauseModal}
      {suspendClauseModal}
      {resumeClauseModal}
    </FlexLayout>
  );
};
export default AutomationHubQuickAiPage;
